import type {
  AccessTokenResult,
  ChangePassword,
  MeResult,
  StaffActor,
  StaffLoginBody,
  StaffLoginResult,
} from '@hadir/shared';

import { request } from '@/lib/api/client';

/**
 * The auth routes, and only the auth routes (04 §4.1).
 *
 * Every other resource (cohorts, courses, sections, students, halls, settings)
 * gets its own module written by the feature owner on top of `request()`; this
 * file stays small so the foundation and the features never fight over it.
 *
 * Request/response shapes are the ones exported by `@hadir/shared` — no DTO is
 * re-declared here.
 */

/** `POST /auth/staff/login` → access token + actor, and sets the `hadir_rt` cookie. */
export function staffLogin(body: StaffLoginBody): Promise<StaffLoginResult> {
  return request<StaffLoginResult>('/auth/staff/login', {
    method: 'POST',
    body,
    auth: false,
  });
}

/**
 * `POST /auth/staff/refresh` — rotates the refresh cookie and returns a new
 * access token.
 *
 * The 401-retry path inside the client calls `refreshSession()` instead (it must
 * not recurse through `request`); this function exists for the boot-time silent
 * restore, where a plain call is exactly right.
 */
export function staffRefresh(): Promise<AccessTokenResult> {
  return request<AccessTokenResult>('/auth/staff/refresh', {
    method: 'POST',
    auth: false,
    // See the note on `keepalive` in `client.ts` — a rotation whose response is
    // dropped costs the user their whole session.
    keepalive: true,
  });
}

/** `POST /auth/staff/logout` → 204. Revokes the refresh token and its whole chain. */
export function staffLogout(): Promise<void> {
  return request<void>('/auth/staff/logout', { method: 'POST', auth: false });
}

/**
 * `POST /auth/staff/change-password` → 204.
 *
 * The API revokes **every** refresh token of the user on success, so the caller
 * must treat the session as finished and send the user back to `/login`.
 */
export function staffChangePassword(body: ChangePassword): Promise<void> {
  return request<void>('/auth/staff/change-password', { method: 'POST', body });
}

/** `GET /auth/me` → `{ actor }`. */
export function fetchMe(): Promise<MeResult> {
  return request<MeResult>('/auth/me');
}

/**
 * The dashboard only ever signs staff in, so a student actor coming back from
 * `/auth/me` (a student token in a dashboard tab) is treated as "no session".
 */
export function staffActorOf(result: MeResult): StaffActor | null {
  return result.actor.type === 'USER' ? result.actor : null;
}
